import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Container,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { AgentService } from '../services/agent.service'
import { useAgent } from '../store/agent.store'
import { useAuth } from '../store/auth.store'
import { useArea } from '../store/area.store'
import { resetCustomerScopedState } from '../utils/sessionReset'
import URLS from '../enums/route'
import { themeColors } from '../styles'

const AgentCustomersPage = () => {
  const navigate = useNavigate()
  const { agent, setImpersonating } = useAgent()
  const { setUser } = useAuth()
  const { setChoosedArea } = useArea()

  const [search, setSearch] = useState('')
  const [customers, setCustomers] = useState<IAgentCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [entering, setEntering] = useState<string | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async (term: string) => {
    setLoading(true)
    setError('')
    try {
      const response = await AgentService.customers(term)
      if (response.status === 'success') {
        setCustomers(response.items)
      } else {
        setError('שגיאה בטעינת לקוחות')
      }
    } catch (e) {
      console.error('[AGENT CUSTOMERS]', e)
      setError('שגיאה בטעינת לקוחות')
    } finally {
      setLoading(false)
    }
  }, [])

  // Debounced so typing a company name does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      load(search)
    }, 350)
    return () => clearTimeout(timer)
  }, [search, load])

  const enterCustomer = async (customer: IAgentCustomer) => {
    setEntering(customer.extId)
    setError('')
    try {
      const response = await AgentService.impersonate(customer.extId)
      if (response.status !== 'success' || !response.user || !response.session) {
        setError(response.message || 'לא ניתן להתחבר ללקוח זה')
        return
      }

      // Clear the previous customer's state BEFORE writing the new one, or the
      // old chosen area survives and the catalog comes back wrong.
      resetCustomerScopedState()
      setUser(response.user)
      setImpersonating(response.session)

      // Same area rule as a real customer login (see LoginForm).
      if (response.area && response.area.length === 1) {
        setChoosedArea(response.area[0])
        navigate(URLS.HOME.LINK)
      } else {
        navigate(URLS.AREA_PAGE.LINK)
      }
    } catch (e) {
      console.error('[AGENT IMPERSONATE]', e)
      setError('שגיאה בהתחברות ללקוח')
    } finally {
      setEntering(null)
    }
  }

  return (
    <Container sx={{ pt: 3, pb: 6 }}>
      <Typography fontWeight={700} fontSize={20} color={themeColors.title} mb={0.5}>
        בחירת לקוח
      </Typography>
      <Typography fontSize={14} color={themeColors.title} mb={2}>
        {agent?.name ? `שלום ${agent.name}` : ''}
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="חיפוש לפי שם, חברה, מספר לקוח או טלפון"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {customers.map((customer) => (
            <ListItem
              key={customer.extId}
              divider
              secondaryAction={
                <Button
                  variant="contained"
                  disabled={customer.isBlocked || entering === customer.extId}
                  onClick={() => enterCustomer(customer)}
                >
                  {entering === customer.extId ? '...' : 'כניסה'}
                </Button>
              }
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <span>{customer.company || customer.name || customer.extId}</span>
                    {customer.isBlocked && <Chip size="small" color="error" label="חסום" />}
                    {!customer.isRegistered && <Chip size="small" label="לא רשום" />}
                  </Box>
                }
                secondary={[customer.extId, customer.city, customer.phone].filter(Boolean).join(' · ')}
              />
            </ListItem>
          ))}
          {!customers.length && (
            <Typography py={4} textAlign="center" color={themeColors.title}>
              לא נמצאו לקוחות
            </Typography>
          )}
        </List>
      )}
    </Container>
  )
}

export default AgentCustomersPage
